import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';

import { type AuthState, Permission } from './auth.types';
import { useAuthStore } from './authStore';

const hasPermission = (
	state: Pick<AuthState, 'currentUser' | 'jwtPayload'>,
	permission: Permission
) => {
	if (permission === Permission.DcrEnabled) {
		return !!state.currentUser?.dcrEnabled;
	}

	return state.jwtPayload
		? state.jwtPayload['custom:permissions'].split(',').includes(permission)
		: false;
};

// ===== Getters =====

export const useIdToken = () => useAuthStore(state => state.idToken); // if used "outside React" - useAuthStore.getState().idToken
export const useAuthUsername = () => useAuthStore(state => state.authUsername);
export const useJwtPayload = () => useAuthStore(state => state.jwtPayload);
export const useCurrentUser = () => useAuthStore(state => state.currentUser);
export const useHasPermission = (permission: Permission) =>
	useAuthStore(state => hasPermission(state, permission));

export const usePermissionCheck = () => {
	const currentUser = useCurrentUser();
	const jwtPayload = useJwtPayload();

	return useCallback(
		(permission: Permission) =>
			hasPermission({ currentUser, jwtPayload }, permission),
		[currentUser, jwtPayload]
	);
};

export const useSetCurrentUser = () =>
	useAuthStore(useShallow(({ setCurrentUser }) => setCurrentUser));
